import { useCallback, useEffect, useRef } from 'react';

import { ProductType } from '@xing-com/crate-common-graphql-types';
import { useTracking } from '@xing-com/crate-communication-tracking';
import type { GetCampaignQuery } from '@xing-com/crate-upsell-graphql';
import { createMembershipProductItems } from '@xing-com/crate-upsell-utils';

import { getDefaultContract } from '../utils/get-default-contract';

type NewDesignTrackingOptions = {
  hasCampaignError: boolean;
  product: ProductType;
  routeCampaign: GetCampaignQuery['results'];
};

type NewDesignTracking = {
  trackActivation: (targetProduct: ProductType) => void;
  trackFeatureDiscovery: (targetProduct: ProductType) => void;
  trackFeatureTab: (targetProduct: ProductType) => void;
};

const productElement = (
  product: ProductType,
  premiumElement: string,
  proJobsElement: string
): string =>
  product === ProductType.Premium ? premiumElement : proJobsElement;

export const useNewDesignTracking = ({
  hasCampaignError,
  product,
  routeCampaign,
}: NewDesignTrackingOptions): NewDesignTracking => {
  const { track } = useTracking();
  const trackedProductRef = useRef<ProductType | undefined>(undefined);
  const displayedErrorProductRef = useRef<ProductType | undefined>(undefined);

  useEffect(() => {
    if (
      !routeCampaign ||
      routeCampaign.contracts.length === 0 ||
      trackedProductRef.current === product
    )
      return;

    const contract = getDefaultContract(routeCampaign.contracts);
    const commonTrackingData = {
      type: 'nwt' as const,
      application: 'wbm',
      context: product,
      sentBy: 'purchase_funnel',
      page: 'purchase_funnel/prodview',
    };

    trackedProductRef.current = product;

    track({
      event: 'viewed_screen',
      eventSchema: 'basic',
      ...commonTrackingData,
    });
    track({
      event: 'product_viewed',
      eventSchema: 'ecommerce',
      currencyCode: contract.price.currency,
      productItems: createMembershipProductItems(product, contract),
      ...commonTrackingData,
    });
  }, [product, routeCampaign, track]);

  useEffect(() => {
    if (!hasCampaignError) {
      displayedErrorProductRef.current = undefined;
      return;
    }

    if (displayedErrorProductRef.current === product) return;

    displayedErrorProductRef.current = product;
    track({
      type: 'nwt',
      application: 'wbm',
      context: product,
      sentBy: 'purchase_funnel',
      page: 'purchase_funnel/error',
      event: 'error_shown',
      eventSchema: 'basic',
      error: 'product_loading_failed_to_fetch_data_for_available_products',
    });
  }, [hasCampaignError, product, track]);

  const trackClick = useCallback(
    (element: string): void => {
      track({
        type: 'nwt',
        application: 'wbm',
        context: product,
        sentBy: 'purchase_funnel',
        page: 'purchase_funnel/prodview',
        event: 'clicked',
        eventSchema: 'basic',
        element,
      });
    },
    [product, track]
  );

  return {
    trackActivation: useCallback(
      (targetProduct: ProductType) =>
        trackClick(
          productElement(targetProduct, 'book_premium', 'book_projobs')
        ),
      [trackClick]
    ),
    trackFeatureDiscovery: useCallback(
      (targetProduct: ProductType) =>
        trackClick(
          productElement(
            targetProduct,
            'viewall_premium_features',
            'viewall_projobs_features'
          )
        ),
      [trackClick]
    ),
    trackFeatureTab: useCallback(
      (targetProduct: ProductType) =>
        trackClick(
          productElement(
            targetProduct,
            'premium_features_tab',
            'projobs_features_tab'
          )
        ),
      [trackClick]
    ),
  };
};
